/* Loyalty rewards reminder bubble.
   Self-contained: no dependency on the theme's modal-content component (that's a
   centered/overlay pattern used by sections/popup.liquid; this is a small
   non-modal, non-focus-stealing notification). */
(function () {
  'use strict';

  var STORAGE_KEY = 'joyberri:rewardsPopupDismissedUntil';
  var SUPPRESSION_DAYS = 7; // Default suggestion — confirm before shipping.
  var SHOW_DELAY_MS = 3000; // Appears 2-4s after load; 3000 sits in the middle.

  // Real hero section markup, confirmed in sections/wls-hero.liquid: its root element
  // renders class="wls-hero" (not every template includes this section, hence the
  // fallback in getScrollDismissThreshold below).
  var HERO_SELECTOR = '.wls-hero';

  // Verified directly in therave.co's shipped overlay.js: "data-rave-close":!0 is set on
  // every one of Rave's own close-button variants (initial popup, reward/milestone screens).
  // Not a guess — it's the one stable, literal attribute in an otherwise minified/hashed bundle.
  var RAVE_CLOSE_SELECTOR = '[data-rave-close]';

  function isSuppressed() {
    try {
      var until = window.localStorage.getItem(STORAGE_KEY);
      if (!until) return false;
      return Date.now() < parseInt(until, 10);
    } catch (e) {
      return false; // localStorage unavailable (private mode, etc.) — fail open.
    }
  }

  function suppressForDays(days) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
    } catch (e) {
      /* Storage unavailable — nothing to persist, popup will just show again next load. */
    }
  }

  function isElementVisible(el) {
    if (!el) return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0 && el.offsetParent === null) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function isRaveOpen() {
    var closers = document.querySelectorAll(RAVE_CLOSE_SELECTOR);
    for (var i = 0; i < closers.length; i++) {
      if (isElementVisible(closers[i])) return true;
    }
    return false;
  }

  // Pinned once at show-time — NOT recomputed on every scroll tick (that instability,
  // combined with mobile browser chrome collapsing mid-scroll, was the root cause of the
  // "dismisses almost immediately" bug in the fixed-viewport-multiplier version).
  function getScrollDismissThreshold() {
    var hero = document.querySelector(HERO_SELECTOR);
    if (hero) {
      // getBoundingClientRect() is viewport-relative; add the current scroll offset to
      // convert the hero's bottom edge into an absolute, document-relative position that
      // stays valid as the page (and window.innerHeight, on mobile) changes underneath it.
      return window.scrollY + hero.getBoundingClientRect().bottom;
    }
    // Fallback for templates with no .wls-hero section (product, collection, cart, etc.):
    // one viewport height past wherever the popup appeared.
    return window.scrollY + window.innerHeight;
  }

  function init() {
    if (isSuppressed()) return;

    var popup = document.querySelector('[data-rewards-popup]');
    if (!popup) return;

    var closeBtn = popup.querySelector('[data-rewards-popup-close]');
    var cta = popup.querySelector('[data-rewards-popup-cta]');
    var announcer = document.getElementById('rewards-popup-announcer');
    var announceText = popup.getAttribute('data-announce-text') || '';

    var shown = false;
    var raveObserver = null;
    var scrollTicking = false;
    var scrollDismissThreshold = null;

    function cleanupListeners() {
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('scroll', onScroll);
      if (raveObserver) {
        raveObserver.disconnect();
        raveObserver = null;
      }
    }

    function dismiss() {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
      suppressForDays(SUPPRESSION_DAYS); // Same suppression path for every dismissal source.
      cleanupListeners();
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') dismiss();
    }

    // Click-outside-dismiss removed: it dismissed on ANY click anywhere else on the page —
    // including clicks on Rave's own close button, since that's trivially "outside" our
    // popup too. Rather than special-case Rave (a maintenance trap for the next widget
    // that gets added), the four remaining paths — X, Esc, scroll-past-hero, CTA — are
    // enough for a non-modal, non-blocking popup like this.

    function onScroll() {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(function () {
        scrollTicking = false;
        if (window.scrollY > scrollDismissThreshold) dismiss(); // Scroll-past counts as "seen" — same suppression as manual dismiss.
      });
    }

    function show() {
      if (shown) return;

      scrollDismissThreshold = getScrollDismissThreshold();

      // If the hero (or one viewport, on hero-less templates) is already behind the user
      // by the time the show-delay elapses, don't flash the popup just to have it vanish
      // on the very next scroll tick — treat it the same as a scroll-dismiss.
      if (window.scrollY > scrollDismissThreshold) {
        suppressForDays(SUPPRESSION_DAYS);
        return;
      }

      shown = true;
      popup.classList.add('is-visible');
      popup.setAttribute('aria-hidden', 'false');

      // Set text at show-time (not at parse-time) so the live region reliably
      // announces it — screen readers announce on mutation, not on becoming visible.
      if (announcer) announcer.textContent = announceText;

      // Deliberately no .focus() call anywhere here — must not steal keyboard focus.
      document.addEventListener('keydown', onKeydown);
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    function watchForRaveToClose() {
      if (raveObserver) return; // Already watching.
      raveObserver = new MutationObserver(function () {
        if (!isRaveOpen()) {
          raveObserver.disconnect();
          raveObserver = null;
          tryShow();
        }
      });
      raveObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    }

    function tryShow() {
      if (shown || isSuppressed()) return;
      if (isRaveOpen()) {
        // Don't show on a fixed delay — wait for Rave's own close signal instead,
        // so this doesn't break if Rave's trigger timing changes later.
        watchForRaveToClose();
        return;
      }
      show();
    }

    if (closeBtn) closeBtn.addEventListener('click', dismiss);
    if (cta) cta.addEventListener('click', dismiss); // Clicking through counts as acknowledged.

    window.setTimeout(tryShow, SHOW_DELAY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
